import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Mail, MessageSquare, Send, History, CreditCard,
  FileText, Settings, Bell, TrendingUp, Users,
  AlertCircle, CheckCircle, Smartphone, ArrowRight,
  BarChart3, Activity, Zap, Eye, Calendar, Clock
} from 'lucide-react'
import { useLang } from '@/hooks/useLang'
import { useAuthStore } from '@/store/authStore'
import { apiService } from '@/services/api'
import { toast } from 'sonner'

// ═══════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════

interface MessageLog {
  id: number
  message_type: string
  recipient_name: string
  status: string
  sent_at: string
  body: string
  sms_count: number
  created_at?: string
}

interface SmsCredit {
  total_credits: number
  used_credits: number
  remaining_credits: number
}

interface DashboardStats {
  total_sent: number
  total_email: number
  total_sms: number
  total_failed: number
  success_rate: number
  credits_remaining: number
  recent_logs: MessageLog[]
}

// ═══════════════════════════════════════════════════════════════════
// TRANSLATIONS
// ═══════════════════════════════════════════════════════════════════

const t = {
  fr: {
    title: 'Tableau de bord — Communication',
    subtitle: 'Vue d\'ensemble des messages et notifications',
    cards: {
      totalSent: 'Messages envoyés',
      emails: 'Emails',
      sms: 'SMS',
      failed: 'Échoués',
      successRate: 'Taux de succès',
      credits: 'Crédits SMS restants',
      thisMonth: 'ce mois',
      last7days: '7 derniers jours',
    },
    sections: {
      quickActions: 'Actions rapides',
      recentHistory: 'Historique récent',
      stats: 'Statistiques',
      noHistory: 'Aucun message envoyé récemment',
      viewAll: "Voir tout l'historique",
    },
    actions: {
      sendEmail: 'Envoyer un email',
      sendSms: 'Envoyer un SMS',
      viewHistory: 'Historique complet',
      manageTemplates: 'Gérer les templates',
      configureProvider: 'Configurer fournisseur SMS',
      autoRules: 'Règles auto',
      buyCredits: 'Acheter des crédits',
    },
    status: {
      sent: 'Envoyé',
      failed: 'Échoué',
      pending: 'En attente',
    },
    chart: {
      title: 'Activité des 7 derniers jours',
      emails: 'Emails',
      sms: 'SMS',
    }
  },
  en: {
    title: 'Dashboard — Communication',
    subtitle: 'Overview of messages and notifications',
    cards: {
      totalSent: 'Messages sent',
      emails: 'Emails',
      sms: 'SMS',
      failed: 'Failed',
      successRate: 'Success rate',
      credits: 'SMS credits remaining',
      thisMonth: 'this month',
      last7days: 'last 7 days',
    },
    sections: {
      quickActions: 'Quick actions',
      recentHistory: 'Recent history',
      stats: 'Statistics',
      noHistory: 'No recent messages sent',
      viewAll: 'View full history',
    },
    actions: {
      sendEmail: 'Send an email',
      sendSms: 'Send an SMS',
      viewHistory: 'Full history',
      manageTemplates: 'Manage templates',
      configureProvider: 'Configure SMS provider',
      autoRules: 'Auto rules',
      buyCredits: 'Buy credits',
    },
    status: {
      sent: 'Sent',
      failed: 'Failed',
      pending: 'Pending',
    },
    chart: {
      title: 'Activity over last 7 days',
      emails: 'Emails',
      sms: 'SMS',
    }
  }
}

// ═══════════════════════════════════════════════════════════════════
// HELPER: Générer données de démo pour le graphique
// ═══════════════════════════════════════════════════════════════════

function generateWeekData() {
  const days = []
  const today = new Date()
  for (let i = 6; i >= 0; i--) {
    const d = new Date(today)
    d.setDate(d.getDate() - i)
    days.push({
      date: d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric' }),
      email: Math.floor(Math.random() * 8) + 1,
      sms: Math.floor(Math.random() * 15) + 2,
    })
  }
  return days
}

// ═══════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════

export default function MessageDashboardPage() {
  const navigate = useNavigate()
  const { lang } = useLang()
  const { user } = useAuthStore()
  const $t = t[lang]

  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [weekData] = useState(generateWeekData)

  const schoolId = (user as any)?.school_id || null

  // Charger les stats depuis le backend + fallback local
  useEffect(() => {
    loadStats()
  }, [schoolId])

  async function loadStats() {
    setLoading(true)
    try {
      // Essayer de charger depuis le backend
      const url = schoolId
        ? `/messages/stats?school_id=${schoolId}&period=month`
        : '/messages/stats?period=month'
      const res = await apiService.get<any>(url)

      // Charger aussi les logs récents
      const logsUrl = schoolId
        ? `/messages/logs?school_id=${schoolId}&per_page=5`
        : '/messages/logs?per_page=5'
      const logsRes = await apiService.get<any>(logsUrl)
      const recentLogs = Array.isArray(logsRes) ? logsRes.slice(0, 5)
        : Array.isArray(logsRes?.items) ? logsRes.items.slice(0, 5)
        : []

      setStats({
        total_sent: res?.total_sent || 0,
        total_email: res?.total_email_sent || 0,
        total_sms: res?.total_sms_sent || 0,
        total_failed: res?.total_failed || 0,
        success_rate: res?.total_sent ? Math.round(((res.total_sent - (res.total_failed || 0)) / res.total_sent) * 100) : 100,
        credits_remaining: res?.credits_remaining || 1000,
        recent_logs: recentLogs,
      })
    } catch (err) {
      console.error('Error loading stats:', err)
      // Fallback complet
      const localLogs = JSON.parse(localStorage.getItem('message_local_logs') || '[]')
      const emailCount = localLogs.filter((l: any) => l.message_type === 'email').length
      const smsCount = localLogs.filter((l: any) => l.message_type === 'sms').length
      const failedCount = localLogs.filter((l: any) => l.status === 'failed').length
      const total = localLogs.length

      setStats({
        total_sent: total,
        total_email: emailCount,
        total_sms: smsCount,
        total_failed: failedCount,
        success_rate: total ? Math.round(((total - failedCount) / total) * 100) : 100,
        credits_remaining: 1000,
        recent_logs: localLogs.slice(0, 5),
      })
    } finally {
      setLoading(false)
    }
  }

  const maxBar = useMemo(() => {
    return Math.max(...weekData.map(d => d.email + d.sms), 1)
  }, [weekData])

  const actions = [
    { id: 'email', label: $t.actions.sendEmail, icon: Mail, color: 'bg-blue-50 text-blue-600 border-blue-200', tab: 'email' },
    { id: 'sms', label: $t.actions.sendSms, icon: Smartphone, color: 'bg-emerald-50 text-emerald-600 border-emerald-200', tab: 'sms' },
    { id: 'history', label: $t.actions.viewHistory, icon: History, color: 'bg-amber-50 text-amber-600 border-amber-200', tab: 'history' },
    { id: 'templates', label: $t.actions.manageTemplates, icon: FileText, color: 'bg-violet-50 text-violet-600 border-violet-200', tab: 'templates' },
    { id: 'provider', label: $t.actions.configureProvider, icon: Settings, color: 'bg-slate-50 text-slate-600 border-slate-200', tab: 'provider' },
    { id: 'auto', label: $t.actions.autoRules, icon: Bell, color: 'bg-rose-50 text-rose-600 border-rose-200', tab: 'auto' },
    { id: 'credits', label: $t.actions.buyCredits, icon: CreditCard, color: 'bg-teal-50 text-teal-600 border-teal-200', tab: 'credits' },
  ]

  const navigateToTab = (tab: string) => {
    navigate('/messages', { state: { activeTab: tab } })
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* HEADER */}
      <header className="bg-white border-b border-gray-200 px-6 py-5">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 bg-[#1e3a8a] rounded-xl flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">{$t.title}</h1>
              <p className="text-sm text-gray-500">{$t.subtitle}</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {/* ═══════════════════ STATS CARDS ═══════════════════ */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                <Send className="w-5 h-5 text-blue-600" />
              </div>
              <span className="text-xs font-medium text-gray-400 bg-gray-100 px-2 py-1 rounded-full">{$t.cards.thisMonth}</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{loading ? '—' : stats?.total_sent || 0}</p>
            <p className="text-sm text-gray-500 mt-1">{$t.cards.totalSent}</p>
          </div>

          <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                <Mail className="w-5 h-5 text-blue-500" />
              </div>
              <span className="text-xs font-medium text-gray-400 bg-gray-100 px-2 py-1 rounded-full">{$t.cards.thisMonth}</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{loading ? '—' : stats?.total_email || 0}</p>
            <p className="text-sm text-gray-500 mt-1">{$t.cards.emails}</p>
          </div>

          <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center">
                <Smartphone className="w-5 h-5 text-emerald-500" />
              </div>
              <span className="text-xs font-medium text-gray-400 bg-gray-100 px-2 py-1 rounded-full">{$t.cards.thisMonth}</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{loading ? '—' : stats?.total_sms || 0}</p>
            <p className="text-sm text-gray-500 mt-1">{$t.cards.sms}</p>
          </div>

          <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-amber-500" />
              </div>
              <span className="text-xs font-medium text-gray-400 bg-gray-100 px-2 py-1 rounded-full">SMS</span>
            </div>
            <p className="text-2xl font-bold text-gray-900">{loading ? '—' : stats?.credits_remaining || 0}</p>
            <p className="text-sm text-gray-500 mt-1">{$t.cards.credits}</p>
          </div>
        </div>

        {/* ═══════════════════ ROW: CHART + RECENT HISTORY ═══════════════════ */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Chart */}
          <div className="lg:col-span-2 bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-[#1e3a8a]" />
                {$t.chart.title}
              </h3>
              <span className="text-xs text-gray-400">{$t.cards.last7days}</span>
            </div>
            <div className="flex items-end gap-3 h-48 px-2">
              {weekData.map((day, i) => {
                const emailH = (day.email / maxBar) * 100
                const smsH = (day.sms / maxBar) * 100
                return (
                  <div key={i} className="flex-1 flex flex-col items-center gap-1">
                    <div className="w-full flex gap-0.5 justify-center" style={{ height: '160px' }}>
                      <div
                        className="w-3 bg-blue-400 rounded-t transition-all"
                        style={{ height: `${emailH}%`, minHeight: '4px' }}
                        title={`${day.email} emails`}
                      />
                      <div
                        className="w-3 bg-emerald-400 rounded-t transition-all"
                        style={{ height: `${smsH}%`, minHeight: '4px' }}
                        title={`${day.sms} SMS`}
                      />
                    </div>
                    <span className="text-[10px] text-gray-500 mt-1">{day.date}</span>
                  </div>
                )
              })}
            </div>
            <div className="flex items-center gap-6 mt-4 justify-center">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-blue-400 rounded" />
                <span className="text-xs text-gray-600">{$t.chart.emails}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-emerald-400 rounded" />
                <span className="text-xs text-gray-600">{$t.chart.sms}</span>
              </div>
            </div>
          </div>

          {/* Recent History */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                <History className="w-5 h-5 text-[#1e3a8a]" />
                {$t.sections.recentHistory}
              </h3>
              <button
                onClick={() => navigateToTab('history')}
                className="text-xs text-[#1e3a8a] hover:underline flex items-center gap-1"
              >
                {$t.sections.viewAll}
                <ArrowRight className="w-3 h-3" />
              </button>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-5 h-5 border-2 border-gray-300 border-t-[#1e3a8a] rounded-full animate-spin" />
              </div>
            ) : !stats?.recent_logs?.length ? (
              <div className="text-center py-8">
                <History className="w-10 h-10 text-gray-200 mx-auto mb-2" />
                <p className="text-sm text-gray-400">{$t.sections.noHistory}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {stats.recent_logs.map((log) => (
                  <div key={log.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      log.message_type === 'email' ? 'bg-blue-100' : 'bg-emerald-100'
                    }`}>
                      {log.message_type === 'email' ? (
                        <Mail className="w-4 h-4 text-blue-600" />
                      ) : (
                        <Smartphone className="w-4 h-4 text-emerald-600" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {log.recipient_name || '—'}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {log.body?.substring(0, 40)}...
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                          log.status === 'sent' ? 'bg-green-100 text-green-700' :
                          log.status === 'failed' ? 'bg-red-100 text-red-700' :
                          'bg-amber-100 text-amber-700'
                        }`}>
                          {log.status === 'sent' ? <CheckCircle className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                          {log.status}
                        </span>
                        <span className="text-[10px] text-gray-400">
                          {new Date(log.sent_at || log.created_at || new Date().toISOString()).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-US')}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ═══════════════════ QUICK ACTIONS ═══════════════════ */}
        <div>
          <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Zap className="w-5 h-5 text-[#1e3a8a]" />
            {$t.sections.quickActions}
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
            {actions.map((action) => {
              const Icon = action.icon
              return (
                <button
                  key={action.id}
                  onClick={() => navigateToTab(action.tab)}
                  className={`flex flex-col items-center gap-2 p-4 rounded-2xl border transition-all hover:shadow-md hover:-translate-y-0.5 ${action.color}`}
                >
                  <Icon className="w-6 h-6" />
                  <span className="text-xs font-medium text-center leading-tight">{action.label}</span>
                </button>
              )
            })}
          </div>
        </div>

        {/* ═══════════════════ SUCCESS RATE + STATUS ═══════════════════ */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
            <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
              <Activity className="w-4 h-4 text-[#1e3a8a]" />
              {$t.cards.successRate}
            </h3>
            <div className="flex items-center gap-4">
              <div className="relative w-20 h-20">
                <svg className="w-20 h-20 -rotate-90" viewBox="0 0 36 36">
                  <path
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                    fill="none"
                    stroke="#e5e7eb"
                    strokeWidth="3"
                  />
                  <path
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                    fill="none"
                    stroke="#10b981"
                    strokeWidth="3"
                    strokeDasharray={`${stats?.success_rate || 0}, 100`}
                  />
                </svg>
                <span className="absolute inset-0 flex items-center justify-center text-sm font-bold text-gray-900">
                  {loading ? '—' : `${stats?.success_rate || 0}%`}
                </span>
              </div>
              <div>
                <p className="text-sm text-gray-600">
                  <span className="font-bold text-green-600">{stats?.total_sent || 0}</span> envoyés
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-bold text-red-500">{stats?.total_failed || 0}</span> échoués
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
            <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
              <Calendar className="w-4 h-4 text-[#1e3a8a]" />
              Aperçu rapide
            </h3>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Templates actifs</span>
                <span className="font-bold text-gray-900">8</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Règles auto</span>
                <span className="font-bold text-gray-900">2</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Fournisseur SMS</span>
                <span className="font-bold text-amber-600">Non configuré</span>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
